import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { TenantContentBlock } from '../types/theme'

interface InfoModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  content: TenantContentBlock
}

export const InfoModal: React.FC<InfoModalProps> = ({ isOpen, onClose, title, content }) => {
  const [imageLoadError, setImageLoadError] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setImageLoadError(false)
      const showTimer = setTimeout(() => setIsVisible(true), 100)
      return () => clearTimeout(showTimer)
    }
    setIsVisible(false)
  }, [isOpen, content.imageUrl])

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const isImageMode = content.type === 'image' && Boolean(content.imageUrl) && !imageLoadError
  const showTextFallback = content.type === 'text' || (content.type === 'image' && (!content.imageUrl || imageLoadError))

  return createPortal(
    <div
      className={`fixed inset-0 flex items-center justify-center transition-all duration-300 ease-out ${isVisible ? 'opacity-100' : 'opacity-0'} p-3 sm:p-4 md:p-6`}
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1050,
        backgroundColor: isImageMode ? 'rgba(2, 6, 23, 0.88)' : 'rgba(2, 6, 23, 0.78)',
        backdropFilter: 'blur(6px)',
        overflowY: 'auto',
      }}
    >
      <div
        className={`relative transform transition-all duration-300 ease-out ${isVisible ? 'scale-100 translate-y-0' : 'scale-95 -translate-y-8'} mx-auto flex flex-col info-modal-panel ${isImageMode ? 'info-modal-panel--image' : ''}`}
        style={{
          maxWidth: isImageMode ? 'min(920px, 96vw)' : '700px',
          width: isImageMode ? 'auto' : '95%',
          height: 'auto',
          maxHeight: isImageMode ? '92vh' : '85vh',
          boxShadow: isImageMode
            ? '0 24px 64px rgba(0, 0, 0, 0.55)'
            : '0 28px 70px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255,255,255,0.08)',
          zIndex: 1060,
          border: isImageMode ? 'none' : '1px solid rgba(125, 211, 252, 0.32)',
          borderRadius: isImageMode ? '12px' : '20px',
          position: 'relative',
          margin: 'auto',
          background: isImageMode
            ? 'transparent'
            : 'radial-gradient(ellipse 80% 50% at 10% 0%, color-mix(in srgb, var(--htd-primary) 28%, transparent), transparent 55%), linear-gradient(180deg, rgba(12, 24, 52, 0.98) 0%, rgba(9, 16, 36, 0.98) 100%)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className={isImageMode ? 'info-modal-close info-modal-close--image' : undefined}
          style={isImageMode ? undefined : {
            position: 'absolute',
            top: '12px',
            right: '12px',
            padding: 0,
            color: 'white',
            fontSize: '16px',
            border: '1px solid rgba(255,255,255,0.35)',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            fontWeight: 'bold',
            backgroundColor: 'color-mix(in srgb, var(--htd-primary) 45%, transparent)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          }}
        >
          ×
        </button>

        {!isImageMode && (
          <div
            className="flex items-center p-6"
            style={{
              background: 'linear-gradient(135deg, var(--htd-primary) 0%, var(--htd-secondary) 100%)',
              borderRadius: '20px 20px 0 0',
              flexShrink: 0,
            }}
          >
            <h3 className="text-xl font-bold text-white flex items-center mb-0">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                <i className="fas fa-info-circle text-white text-sm" />
              </div>
              {title}
            </h3>
          </div>
        )}

        <div
          className={`overflow-y-auto flex-1 ${isImageMode ? 'info-modal-body--image' : ''}`}
          style={isImageMode ? {
            background: 'transparent',
            borderRadius: '12px',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            scrollBehavior: 'smooth',
            minHeight: 0,
          } : {
            background: 'rgba(248, 250, 252, 0.98)',
            borderRadius: '0 0 20px 20px',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            scrollBehavior: 'smooth',
            minHeight: 0,
          }}
        >
          {showTextFallback ? (
            <div className="p-4 sm:p-6">
              <div
                className="leading-relaxed"
                style={{ fontSize: '14px', lineHeight: '1.7', color: '#1e293b' }}
                dangerouslySetInnerHTML={{
                  __html:
                    content.textContent ||
                    '<p style="color: #64748b; font-style: italic; text-align: center; padding: 2rem;">ยังไม่มีข้อมูล</p>',
                }}
              />
            </div>
          ) : (
            <div className="info-modal-image-wrap">
              <img
                src={content.imageUrl}
                alt={title}
                className="info-modal-image"
                onError={() => setImageLoadError(true)}
                onLoad={() => setImageLoadError(false)}
              />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
